// Derives vendor-PO send-status for a confirmed order (order-level, coverage-aware) and for a
// single line item (item-level). Pure — the caller supplies canonicalOf so matching stays
// consistent with the rest of the app (utils/supplierDedup) and the logic stays unit-testable.

// 'amended' (D81): with the supplier, but changed since it was sent — needs a re-send.
// 'invoiced' / 'client_invoice_sent' (item 26, QA retest 2026-10-09): the order has moved past
// supplier POs to client invoicing — these outrank every supplier-PO state, so the list label
// does not stay on "Sent" (POs) forever once there is more to say.
export type PoSendStatus = 'na' | 'none' | 'draft' | 'partial' | 'sent' | 'amended' | 'invoiced' | 'client_invoice_sent';

/** Item 26 — the one client-invoice fact the order-level label needs; read by `listConfirmedOrders`. */
export interface OrderInvoiceInfo {
  /** A (non-additional) ClientInvoice exists for this order. */
  exists: boolean;
  /** Its status is 'sent' or further along (`SENT_ONWARD_STATUSES`, clientInvoice.service.js). */
  sent: boolean;
}

export interface PoLike {
  vendorName?: string;
  status?: string; // 'draft' | 'sent' | 'amended' | 'billed' | 'cancelled'
}

// With the supplier: 'sent', or 'amended' (sent, changed since) — the server's isWithSupplier.
// 'billed' (D144: invoiced in full) is still a sent PO, so it counts here too.
const isWithSupplier = (p: PoLike) => p.status === 'sent' || p.status === 'amended' || p.status === 'billed';

// Mirrors PSEUDO_VENDOR_NAMES in src/utils/supplierName.js — these placeholder
// "vendors" (in-house catalogue rows, unassigned lines, client-supplied items) never get a real
// vendor PO, so they must never count toward order/item PO send-status. "historical quote" is
// the supplier dropdown's old label for a historical row with no source (D105.4).
const PSEUDO_VENDOR_NAMES = new Set([
  'unknown vendor',
  'master catalogue',
  'master catalog',
  'price list',
  'client',
  'historical quote',
]);

// A placeholder the server stores when no real vendor is attributed to a line.
// Also the quotation Supplier column's owner for "is this a supplier name?"
// (USC-BUG-0021: "Master Catalogue" read as if it were a supplier).
export function isPseudoVendorName(name: string | null | undefined): boolean {
  return PSEUDO_VENDOR_NAMES.has(String(name || '').trim().toLowerCase());
}

// Real only if it canonicalizes to something AND its raw (lowercased) name isn't a known
// pseudo-vendor. Match the raw name, not the canonical label — canonicalization could otherwise
// merge a pseudo name into a real vendor's bucket.
function isRealVendor(name: string, canonicalOf: (n: string) => string): boolean {
  return Boolean(canonicalOf(name || '')) && !isPseudoVendorName(name);
}

/**
 * Order-level status across the order's vendor fan-out. `expectedVendors` is the order's vendor
 * split (ConfirmedOrder.vendors); `pos` are the vendor POs for THIS order. "partial" fires when
 * some expected vendors are sent but not all — covering both draft POs and vendors with no PO yet.
 * Pseudo-vendors (e.g. "Master Catalogue") are excluded from `expectedVendors` and from any POs;
 * an order made up entirely of pseudo-vendors with no real POs owes nothing, so it returns "na".
 */
export function orderPoSendStatus(
  expectedVendors: string[],
  pos: PoLike[],
  canonicalOf: (n: string) => string,
  invoiceInfo?: OrderInvoiceInfo,
): PoSendStatus {
  // Item 26 — client invoicing is further along than any supplier-PO state, so it is
  // checked first and wins outright; the PO-only reading below is unchanged otherwise.
  if (invoiceInfo?.sent) return 'client_invoice_sent';
  if (invoiceInfo?.exists) return 'invoiced';

  const active = pos.filter((p) => p.status !== 'cancelled' && isRealVendor(p.vendorName || '', canonicalOf));
  const created = new Set(active.map((p) => canonicalOf(p.vendorName || '')).filter(Boolean));
  const sent = new Set(
    active.filter(isWithSupplier).map((p) => canonicalOf(p.vendorName || '')).filter(Boolean),
  );
  const expected = new Set(
    expectedVendors.filter((v) => isRealVendor(v, canonicalOf)).map((v) => canonicalOf(v)),
  );
  const denom = expected.size ? expected : created;

  if (denom.size === 0 && created.size === 0) return 'na';
  if (created.size === 0) return 'none';
  if (denom.size > 0 && Array.from(denom).every((v) => sent.has(v))) {
    return active.some((p) => p.status === 'amended') ? 'amended' : 'sent';
  }
  if (sent.size > 0) return 'partial';
  return 'draft';
}

/** Item-level status: the send-state of the PO for this item's vendor. */
export function itemPoSendStatus(
  vendorName: string,
  pos: PoLike[],
  canonicalOf: (n: string) => string,
): PoSendStatus {
  if (!isRealVendor(vendorName, canonicalOf)) return 'na';
  const v = canonicalOf(vendorName || '');
  const po = pos.find((p) => p.status !== 'cancelled' && canonicalOf(p.vendorName || '') === v);
  if (!po) return 'none';
  if (po.status === 'amended') return 'amended';
  return po.status === 'sent' || po.status === 'billed' ? 'sent' : 'draft';
}

export const PO_STATUS_BADGE: Record<PoSendStatus, { label: string; className: string }> = {
  na: { label: '—', className: 'text-slate-300' },
  none: { label: 'Pending', className: 'bg-slate-100 text-slate-500 border-slate-200' },
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700 border-slate-300' },
  partial: { label: 'Partially sent', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  sent: { label: 'Sent', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  amended: { label: 'Amended — re-send', className: 'bg-amber-50 text-amber-800 border-amber-200' },
  invoiced: { label: 'Invoiced', className: 'bg-sky-50 text-sky-700 border-sky-200' },
  client_invoice_sent: { label: 'Sent', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

/**
 * D104.25 — what the Confirmed Orders LIST column ("Supplier POs") says per order. The list's
 * badge is the supplier-PO state, not the order's status, so its words say so; tone comes from
 * StatusBadge's `toneForStatus` (one map) — each phrase below is chosen to classify correctly
 * there ("Drafts only" and "Partially sent" read as attention, "Sent" as done).
 */
export const ORDER_PO_STATUS_LABEL: Record<PoSendStatus, string> = {
  na: 'No supplier chosen', // no real supplier on any line yet — nothing to raise a PO to
  none: 'No POs yet',
  draft: 'Drafts only',
  partial: 'Partially sent',
  sent: 'Sent',
  amended: 'Amended — re-send',
  // Item 26 — the order moved past supplier POs to client invoicing; "done" (toneForStatus).
  invoiced: 'Invoiced',
  client_invoice_sent: 'Sent',
};
